// 261010 Epic 2 / P2b-2 — bước GHÉP (cấp plan) của khung mới, CÓ thao tác: dựng / dựng lại cả tập + ô nhạc nền (URL, tuỳ chọn) + xem bản dựng.
// Viết mới (không mount `FilmExportTab`: nó buộc phải có `FilmPlanProvider`); gọi thẳng `filmApi.getExport / assemblePlan`.
// Nút Dựng KHOÁ khi còn video chưa chọn clip (server vốn ném lỗi, UI cũ vẫn cho bấm). Không có nút "Duyệt bản dựng" (đổi `plan.status` là trạng thái legacy). KHÔNG hiện giá.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Input, Popconfirm, Space, Spin, message } from 'antd';
import { filmApi } from '../../services/api/film';
import type { EventPlan, FilmExportMeta, FilmExportStatus } from '../../types/film';
import { useAliveRef } from '../../hooks/useAliveRef';
import { apiErrorText } from './apiErrorText';
import { truncateText } from './workflowBoardUtils';
import {
  actionOf, actionsFor, assemblyConfirmContent, assemblyCtxOf, VIDEO_JOB_KIND, workflowActionError,
  type ActionError, type WorkflowActionCtx,
} from './workflowActionPolicy';
import { RunningTag, StepErrorAlert, type InteractiveProps } from './WorkflowStepShared';
import { AssemblyClipWarningList } from './ClipReviewRow'; // 261010 P6: chỉ dòng cảnh báo chữ
import WorkflowAssemblyTimeline from './WorkflowAssemblyTimeline';

interface Props extends InteractiveProps {
  detail: EventPlan | null;
}
interface ExportInfo { exportUrl: string | null; exportStatus: FilmExportStatus | null; exportMeta: FilmExportMeta | null }

const POLL_MS = 8000;
const STATUS_TEXT: Record<string, string> = { rendering: 'đang dựng…', done: 'đã dựng', failed: 'dựng lỗi', pending: 'chưa dựng' };

const WorkflowAssemblyStep: React.FC<Props> = ({ planId, detail, jobs, failures, events, onOpenEvent, onActionDone, onJobsRefresh, assemblyClipWarnings }) => {
  const alive = useAliveRef();
  const [exp, setExp] = useState<ExportInfo | null>(null);
  const [expErr, setExpErr] = useState<string | null>(null);
  const [err, setErr] = useState<ActionError | null>(null);
  const [bgm, setBgm] = useState('');
  const [starting, setStarting] = useState(false);
  const inflight = useRef(false);

  const fetchExport = useCallback(async () => {
    if (inflight.current) return; // một lượt >8 s thì không gọi chồng
    inflight.current = true;
    try {
      const r = await filmApi.getExport(planId);
      if (alive.current) { setExp(r); setExpErr(null); }
    } catch (e: any) { if (alive.current) setExpErr(apiErrorText(e, 'Tải bản dựng')); }
    finally { inflight.current = false; }
  }, [planId, alive]);
  useEffect(() => { fetchExport(); }, [fetchExport]);

  // Việc dựng vừa xong (job mất khỏi `workflow-status.jobs`): nạp lại bản dựng + báo trang tải lại (`jobsFinished` của trang chỉ nạp lại plan / storyboard / sơ đồ, không nạp export).
  const assembleRunning = jobs.some((j) => j.type === VIDEO_JOB_KIND.ASSEMBLE);
  const prevRunning = useRef(assembleRunning);
  useEffect(() => {
    if (prevRunning.current && !assembleRunning) { fetchExport(); onActionDone(); }
    prevRunning.current = assembleRunning;
  }, [assembleRunning, fetchExport, onActionDone]);

  const rendering = exp?.exportStatus === 'rendering' || assembleRunning;
  useEffect(() => {
    if (!rendering) return undefined;
    const t = window.setInterval(() => { fetchExport(); }, POLL_MS);
    return () => window.clearInterval(t);
  }, [rendering, fetchExport]);
  const prevStatus = useRef<FilmExportStatus | null | undefined>(undefined);
  useEffect(() => {
    if (prevStatus.current === 'rendering' && exp && exp.exportStatus !== 'rendering') onActionDone(); // dựng xong / lỗi ⇒ tải lại failures + plan (exportStale)
    prevStatus.current = exp?.exportStatus;
  }, [exp, onActionDone]);

  const detailEvents = useMemo(() => (detail ? detail.scenes.flatMap((s) => s.events) : null), [detail]);
  const asm = useMemo(
    () => assemblyCtxOf(events ?? [], detailEvents, { exportStatus: exp?.exportStatus, exportUrl: exp?.exportUrl ?? detail?.plan.exportUrl, exportStale: detail?.plan.exportStale }, jobs),
    [events, detailEvents, exp, detail, jobs],
  );
  const ctx: WorkflowActionCtx = useMemo(() => ({ isSky: true, stepState: 'NOT_READY', runningJobKinds: [], assembly: asm }), [asm]);
  const actions = useMemo(() => actionsFor('ASSEMBLY', ctx), [ctx]);
  const act = actionOf(actions, asm.hasExport ? 'ASM_REASSEMBLE' : 'ASM_ASSEMBLE');

  const doAssemble = async () => {
    setErr(null); setStarting(true);
    try {
      await filmApi.assemblePlan(planId, { bgmUrl: bgm.trim() || undefined }); // KHÔNG gửi `force` (= AI nghe lại để tạo lại phụ đề)
      message.info('Đã gửi lệnh dựng — chạy ở nền, mất vài phút.');
      await onJobsRefresh();
      await fetchExport();
    } catch (e: any) { if (alive.current) setErr(workflowActionError(e, 'Dựng cả tập')); }
    finally { if (alive.current) setStarting(false); onActionDone(); }
  };

  const lastFail = failures.filter((f) => f.type === VIDEO_JOB_KIND.ASSEMBLE).sort((a, b) => a.minutesAgo - b.minutesAgo)[0];
  const failText = exp?.exportStatus === 'failed' ? (exp.exportMeta?.error || lastFail?.note || 'không rõ lý do') : null;
  const meta = exp?.exportMeta;
  const url = exp?.exportUrl ?? detail?.plan.exportUrl ?? null;
  const confirm = assemblyConfirmContent(asm);

  if (!detail) {
    return <div style={{ padding: 8 }}><Spin size="small" /> <span className="muted">Đang tải chi tiết plan…</span> <Button size="small" onClick={onActionDone}>Tải lại</Button></div>;
  }

  return (
    <div data-testid="wf-assembly-step">
      <StepErrorAlert error={err} onReload={onActionDone} onClose={() => setErr(null)} />
      {expErr && <Alert type="error" showIcon style={{ marginBottom: 8 }} message={expErr} action={<Button size="small" onClick={fetchExport}>Tải lại</Button>} />}
      <Space wrap size={8} style={{ marginBottom: 10 }}>
        <span className="muted">Bản dựng: <b>{STATUS_TEXT[exp?.exportStatus ?? (url ? 'done' : 'pending')] ?? '—'}</b></span>
        {rendering && <RunningTag>đang dựng ở nền…</RunningTag>}
        {asm.exportStale && url && <span className="wf-chip warn" data-testid="wf-export-stale">bản dựng đã cũ</span>}
      </Space>
      {asm.exportStale && url && <p className="wf-alert WARN">Bản dựng đã cũ — có clip hoặc nội dung đổi sau lần dựng gần nhất. Dựng lại để cập nhật.</p>}
      {failText && (
        <Alert type="error" showIcon style={{ marginBottom: 10 }} data-testid="wf-export-failed" message="Dựng cả tập lỗi" description={truncateText(failText, 400)}
          action={<Button size="small" onClick={() => { fetchExport(); onActionDone(); }}>Tải lại</Button>} />
      )}

      {url ? <video src={url} controls preload="metadata" style={{ width: '100%', maxWidth: 360, aspectRatio: '9 / 16', borderRadius: 10, background: '#000' }} data-testid="wf-export-video" /> : <p className="muted">Chưa có bản dựng.</p>}
      {meta && (meta.durationSec || meta.clipCount || meta.subtitledShots != null) ? (
        <div className="wf-kv" data-testid="wf-export-meta">
          {meta.durationSec != null && <span>Thời lượng: <b className="mono">{Math.round(meta.durationSec)}s</b></span>}
          {meta.clipCount != null && <span>Số clip: <b>{meta.clipCount}</b></span>}
          {meta.subtitledShots != null && <span>Clip có phụ đề: <b>{meta.subtitledShots}</b></span>}
          {meta.renderMs != null && <span>Dựng trong: <b className="mono">{Math.round(meta.renderMs / 1000)}s</b></span>}
        </div>
      ) : null}

      <h3 style={{ marginTop: 14 }}>Chuẩn bị</h3>
      {events && events.length > 0 ? <WorkflowAssemblyTimeline events={events} onOpen={(id) => onOpenEvent?.(id)} /> : null}
      <p style={{ margin: '4px 0' }} data-testid="wf-asm-count">Đã chọn clip: <b>{asm.withSelected}/{asm.total}</b> video</p>
      {asm.missing.length > 0 && (
        <ul className="wf-list" data-testid="wf-asm-missing">
          {asm.missing.map((m) => (
            <li key={m.eventId}>
              <b>Video {m.eventOrder}</b> chưa có clip được chọn{' '}
              {onOpenEvent && <Button size="small" type="link" onClick={() => onOpenEvent(m.eventId)}>Mở bước Video của Video {m.eventOrder}</Button>}
            </li>
          ))}
        </ul>
      )}

      <AssemblyClipWarningList warnings={assemblyClipWarnings} />

      <div style={{ margin: '10px 0 6px' }}>
        <div className="muted" style={{ fontSize: 12, marginBottom: 4 }}>Nhạc nền (URL, tuỳ chọn)</div>
        <Input size="small" style={{ maxWidth: 420 }} value={bgm} onChange={(e) => setBgm(e.target.value)} placeholder="https://… (để trống: dùng nhạc nền mặc định nếu có)" disabled={rendering} aria-label="Nhạc nền" />
      </div>
      {act && (
        <Space wrap size={8} style={{ marginTop: 6 }}>
          <Popconfirm title={confirm.title} description={<div style={{ maxWidth: 320 }}>{confirm.body}</div>} okText={confirm.okText} cancelText={confirm.cancelText}
            disabled={!act.enabled} onConfirm={doAssemble}>
            <Button type="primary" size="small" loading={starting} disabled={!act.enabled} title={act.enabled ? undefined : act.reason} data-testid="wf-asm-button">{act.label}</Button>
          </Popconfirm>
          {!act.enabled && <span className="muted" style={{ fontSize: 12 }} data-testid="wf-asm-reason">{act.reason}</span>}
        </Space>
      )}
      <p className="wf-note" style={{ marginTop: 12 }}>Bước này chưa có "đã duyệt": hệ thống không lưu việc người duyệt bản dựng.</p>
    </div>
  );
};

export default WorkflowAssemblyStep;
